import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  MediaRecord, 
  MediaRecordUpdateForm, 
  APIResponse, 
  UseMediaRecordResult 
} from '../types/mediaRecord';

// API service functions
const mediaRecordApi = {
  get: async (mediaId: string, tenantId: string): Promise<MediaRecord> => {
    const authToken = getAuthToken();
    const headers: Record<string, string> = {
      'X-Tenant-ID': tenantId,
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`/api/media/${mediaId}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch media record: ${response.statusText}`);
    }

    const result: APIResponse<MediaRecord> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.message || 'Failed to fetch media record');
    }

    return result.data;
  },

  update: async (
    mediaId: string, 
    tenantId: string, 
    updates: MediaRecordUpdateForm
  ): Promise<MediaRecord> => {
    const authToken = getAuthToken();
    const headers: Record<string, string> = {
      'X-Tenant-ID': tenantId,
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`/api/media/${mediaId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error(`Failed to update media record: ${response.statusText}`);
    }

    const result: APIResponse<MediaRecord> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.message || 'Failed to update media record');
    }

    return result.data;
  },
};

// Helper functions
const getAuthToken = (): string => {
  return localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || '';
};

/**
 * Custom hook for managing media record data
 */
export const useMediaRecord = (mediaId: string): UseMediaRecordResult => {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const { user } = useAuth();
  
  // Get tenant ID from context, fallback to user's tenant, then development default
  const tenantId = currentTenant?.id || user?.tenantId || 'creator';
  
  // Debug logging for development
  if (import.meta.env.DEV) {
    console.log('🔍 Media Record Hook Debug:', {
      mediaId,
      tenantId,
      currentTenant: currentTenant?.id,
      userTenantId: user?.tenantId,
      finalTenantId: tenantId
    });
  }

  // Query for fetching media record
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['mediaRecord', mediaId, tenantId],
    queryFn: () => mediaRecordApi.get(mediaId, tenantId),
    enabled: !!mediaId && !!tenantId,
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes (formerly cacheTime)
    retry: (failureCount, error) => {
      // Don't retry for 404s or auth errors
      if (error.message.includes('404') || error.message.includes('401')) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Mutation for updating media record
  const updateMutation = useMutation({
    mutationFn: (updates: MediaRecordUpdateForm) =>
      mediaRecordApi.update(mediaId, tenantId, updates),
    onMutate: async (updates) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['mediaRecord', mediaId, tenantId] });

      // Snapshot the previous value
      const previousRecord = queryClient.getQueryData<MediaRecord>(['mediaRecord', mediaId, tenantId]);

      // Optimistically update to the new value
      if (previousRecord) {
        queryClient.setQueryData(['mediaRecord', mediaId, tenantId], {
          ...previousRecord,
          ...updates,
          updated_at: new Date().toISOString(),
        });
      }

      // Return a context object with the snapshotted value
      return { previousRecord };
    },
    onError: (error, updates, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousRecord) {
        queryClient.setQueryData(['mediaRecord', mediaId, tenantId], context.previousRecord);
      }
      
      toast.error(`Failed to update media record: ${error.message}`);
    },
    onSuccess: (updatedRecord) => {
      // Update the cache with the server response
      queryClient.setQueryData(['mediaRecord', mediaId, tenantId], updatedRecord);
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['mediaRecord', mediaId] });
      queryClient.invalidateQueries({ queryKey: ['mediaAnalytics', mediaId] });
      
      toast.success('Media record updated successfully');
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['mediaRecord', mediaId, tenantId] });
    },
  });

  const update = async (updates: MediaRecordUpdateForm): Promise<void> => {
    updateMutation.mutateAsync(updates);
  };

  return {
    data,
    isLoading,
    error: error as Error | null,
    refetch,
    update,
    isUpdating: updateMutation.isPending,
  };
};

/**
 * Hook for prefetching media record data
 */
export const usePrefetchMediaRecord = () => {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const { user } = useAuth();
  const tenantId = currentTenant?.id || user?.tenantId || 'creator';

  const prefetchMediaRecord = (mediaId: string) => {
    queryClient.prefetchQuery({
      queryKey: ['mediaRecord', mediaId, tenantId],
      queryFn: () => mediaRecordApi.get(mediaId, tenantId),
      staleTime: 30000,
    });
  };

  return { prefetchMediaRecord };
};

/**
 * Hook for invalidating media record cache
 */
export const useInvalidateMediaRecord = () => {
  const queryClient = useQueryClient();

  const invalidateMediaRecord = (mediaId: string) => {
    queryClient.invalidateQueries({ queryKey: ['mediaRecord', mediaId] });
  };

  const invalidateAllMediaRecords = () => {
    queryClient.invalidateQueries({ queryKey: ['mediaRecord'] });
  };

  return { invalidateMediaRecord, invalidateAllMediaRecords };
};